import { observer } from 'mobx-react-lite';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { Card, CardContent, PageLayout } from '../components/shared';
import { TransactionEventType } from '../features/billing/api/types';
import {
  ActionsBar,
  BalanceCards,
  BalancesSearch,
  BillingTabs,
  ChargeForm,
  DepositForm,
  Filters,
  TransactionList,
  UserBalancesList,
} from '../features/billing/components';
import { isUserAdmin } from '../lib/userUtils';
import { useBillingStore, useUserStore } from '../stores';

const Billing: React.FC = observer(() => {
  const params = useParams();
  const { workspaceSlug } = params;
  const { t } = useTranslation();
  const userStore = useUserStore();
  const billingStore = useBillingStore();

  const [selectedFilter, setSelectedFilter] =
    useState<TransactionEventType | null>(null);
  const [isDepositFormOpen, setIsDepositFormOpen] = useState(false);
  const [isChargeFormOpen, setIsChargeFormOpen] = useState(false);

  const isAdmin = userStore.user?.roles
    ? isUserAdmin(userStore.user.roles)
    : false;
  const currentUserId = userStore.userId;

  useEffect(() => {
    if (currentUserId) {
      billingStore.initialize(isAdmin ? undefined : currentUserId);
    }
  }, [currentUserId, isAdmin, billingStore]);

  const handleFilterChange = (filter: TransactionEventType | null) => {
    setSelectedFilter(filter);
    billingStore.setTransactionType(filter);
    billingStore.reloadPaymentsSilently();
  };

  const handleUserIdChange = (userId: string | null) => {
    billingStore.setUserId(userId);
    billingStore.reloadPaymentsSilently();
  };

  const handleLoadMore = () => {
    if (billingStore.hasMore && !billingStore.isLoading) {
      billingStore.loadPayments(false);
    }
  };

  const handleDepositSubmit = async (
    amount: number,
    comment?: string,
    userId?: string
  ) => {
    return billingStore.createDeposit({ amount, comment, userId });
  };

  const handleChargeSubmit = async (
    amount: number,
    comment: string,
    userId?: string
  ) => {
    return billingStore.createCharge({ amount, comment, userId });
  };

  const handleTabChange = (tab: 'transactions' | 'balances') => {
    billingStore.setActiveTab(tab);
  };

  const handleSortChange = (
    newSortBy: 'name' | 'balance',
    newSortOrder: 'asc' | 'desc'
  ) => {
    billingStore.setBalancesSort(newSortBy, newSortOrder);
  };

  const handleSearchChange = (query: string) => {
    billingStore.setBalancesSearchQuery(query);
  };

  const usersList = Array.from(userStore.users.values()).map((user) => ({
    id: user.id,
    name: user.name,
    email: user.email || undefined,
    photoUrl: user.photoUrl || undefined,
  }));

  if (!workspaceSlug) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 pb-20 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading workspace...</p>
        </div>
      </div>
    );
  }

  if (!currentUserId) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 pb-20 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading user data...</p>
        </div>
      </div>
    );
  }

  const mainBalance = billingStore.currentUserBalance;
  const pendingBalance = billingStore.currentUserPendingBalance;

  return (
    <PageLayout
      title={t('billing.title')}
      subtitle={t('billing.subtitle')}
      icon="💳"
    >
      <BalanceCards
        isAdmin={isAdmin}
        mainBalance={mainBalance}
        pendingBalance={pendingBalance}
        totalPendingBalance={billingStore.totalPendingBalance}
      />

      <ActionsBar
        onOpenDeposit={() => setIsDepositFormOpen(true)}
        onOpenCharge={() => setIsChargeFormOpen(true)}
        depositLabel={t('billing.actions.deposit')}
        chargeLabel={t('billing.actions.charge')}
      />

      {isAdmin && (
        <BillingTabs
          activeTab={billingStore.activeTab}
          onTabChange={handleTabChange}
        />
      )}

      <Card className="shadow-xl">
        <CardContent className="p-6">
          {billingStore.activeTab === 'transactions' ? (
            <>
              <Filters
                selected={selectedFilter}
                onChange={handleFilterChange}
                labels={{
                  all: t('billing.transactions.filter.all'),
                  deposits: t('billing.transactions.filter.deposits'),
                  eventCharges: t('billing.transactions.filter.eventCharges'),
                  charges: t('billing.transactions.filter.charges'),
                }}
                isAdmin={isAdmin}
                users={usersList}
                selectedUserId={billingStore.currentUserId}
                onUserIdChange={handleUserIdChange}
                userFilterPlaceholder={t(
                  'billing.transactions.filter.allUsers'
                )}
              />

              <TransactionList
                payments={billingStore.filteredPayments}
                isLoading={billingStore.isLoading}
                hasMore={billingStore.hasMore}
                onLoadMore={handleLoadMore}
                workspaceSlug={workspaceSlug}
                isAdmin={isAdmin}
                emptyTitle={t('billing.transactions.noTransactions')}
                loadingText={t('common.loading')}
                loadMoreText={t('billing.transactions.loadMore')}
              />
            </>
          ) : (
            <>
              <BalancesSearch
                searchQuery={billingStore.balancesSearchQuery}
                onSearchChange={handleSearchChange}
              />
              <UserBalancesList
                users={userStore.users}
                userAccounts={billingStore.userAccounts}
                isLoading={billingStore.isLoadingAccounts}
                sortBy={billingStore.balancesSortBy}
                sortOrder={billingStore.balancesSortOrder}
                searchQuery={billingStore.balancesSearchQuery}
                onSortChange={handleSortChange}
              />
            </>
          )}
        </CardContent>
      </Card>

      <DepositForm
        isOpen={isDepositFormOpen}
        onClose={() => setIsDepositFormOpen(false)}
        onSubmit={handleDepositSubmit}
        isAdmin={isAdmin}
        users={usersList}
        isSubmitting={billingStore.isSubmitting}
      />

      <ChargeForm
        isOpen={isChargeFormOpen}
        onClose={() => setIsChargeFormOpen(false)}
        onSubmit={handleChargeSubmit}
        isAdmin={isAdmin}
        users={usersList}
        isSubmitting={billingStore.isSubmitting}
      />
    </PageLayout>
  );
});

export default Billing;
